import React, { useState } from 'react';
import EditItem from './EditItem';
import { useLanguage } from '../contexts/LanguageContext';

function Preorder({ items, theme, onRemoveItem, onEditItem, onQtyChange, onSendOrder }) {
    const { t } = useLanguage();
    const [editingIdx, setEditingIdx] = useState(null);
    const [editItem, setEditItem] = useState(null);

    // If qty is not present, default to 1
    const getQty = item => typeof item.qty === 'number' ? item.qty : 1;

    // Debug: log items to verify selectedOptions and price structure
    // console.log('Preorder items:', items);

    // Helper to calculate item price including option adjustments
    const getItemTotalPrice = item => {
        if (item.adjustedPrice !== undefined && item.adjustedPrice !== null) {
            return Number(item.adjustedPrice).toFixed(2);
        }
        return Number(item.price || 0).toFixed(2);
    };

    const getDisplayName = item => {
        return item.displayName || item.name || t('unnamedItem');
    }

    // Helper to get selected option name if not default
    const getSelectedOptionName = item => {
        if (item.selectedOptions) {
            if (Array.isArray(item.selectedOptions)) {
                const nonDefault = item.selectedOptions.find(opt => opt.selected && !opt.is_default);
                return nonDefault ? nonDefault.name : null;
            } else if (typeof item.selectedOptions === 'object') {
                if (item.selectedOptions.selected && !item.selectedOptions.selected.is_default) {
                    return item.selectedOptions.selected.name;
                }
            }
        }
        return null;
    };

    const handleEditClick = (idx, item) => {
        setEditingIdx(idx);
        setEditItem(item);
    };

    // When saving edited item, ensure updated option and price are passed to Preorder
    const handleEditSave = updatedItem => {
        // If option was changed, recalculate adjustedPrice and displayName
        let adjustedPrice = Number(updatedItem.price) || 0;
        let displayName = updatedItem.name;
        if (updatedItem.selectedOptions && updatedItem.selectedOptions.option) {
            if (updatedItem.selectedOptions.option.price_adjust) {
                adjustedPrice += Number(updatedItem.selectedOptions.option.price_adjust) || 0;
            }
            // Update displayName to include option name if not default
            if (!updatedItem.selectedOptions.option.is_default) {
                displayName = `${updatedItem.name} (${updatedItem.selectedOptions.option.name})`;
            }
        }
        const itemToSave = {
            ...updatedItem,
            adjustedPrice,
            displayName,
        };
        if (onEditItem) onEditItem(editingIdx, itemToSave);
        setEditingIdx(null);
        setEditItem(null);
    };

    const handleEditCancel = () => {
        setEditingIdx(null);
        setEditItem(null);
    };

    if (editingIdx !== null && editItem) {
        return (
            <EditItem
                item={editItem}
                theme={theme}
                onSave={handleEditSave}
                onCancel={handleEditCancel}
            />
        );
    }

    return (
        <div style={{
            marginBottom: '1.5rem',
            padding: '0rem',
            background: theme?.colors?.background,
            color: theme?.colors?.text,
            borderRadius: theme?.borderRadius || 8,
            boxShadow: theme?.boxShadow,
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'visible'
        }}>
            {/* <h3 style={{ color: theme?.colors?.primary, marginTop: 0 }}>Preorder Items</h3> */}
            {items && items.length > 0 ? (
                
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                     <h3 style={{ color: theme?.colors?.primary, marginTop: 0 }}>{t('preorderItems')}</h3>
                    {items.map((item, idx) => (
                        <li key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            width: '100%',
                            marginBottom: '0.5rem',
                            flexDirection: 'column',
                            backgroundColor: item.dontMake ? 'rgba(255, 87, 34, 0.5)' : 'transparent',
                            // border: item.dontMake ? '1px solid #ff5722' : 'none',
                            borderRadius: theme?.borderRadius || 6,
                            boxSizing: 'border-box',
                            overflow: 'hidden'

                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                                {/* Edit button to the left of item name */}
                                <button
                                    style={{
                                        marginRight: '0.5rem',
                                        background: theme?.colors?.secondary || '#888',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 6,
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        padding: '0.25rem 0.5rem',
                                        fontSize: '1rem',
                                    }}
                                    title={t('editItem')}
                                    onClick={() => handleEditClick(idx, item)}
                                >
                                    ✏️
                                </button>
                                {/* Qty controls */}
                                <button
                                    style={{
                                        background: theme?.colors?.primary,
                                        color: theme?.colors?.surface,
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 6,
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        padding: '0.25rem 0.5rem',
                                        marginRight: '0.25rem',
                                        fontSize: '1rem',
                                    }}
                                    title={t('decreaseQuantity')}
                                    onClick={() => onQtyChange && onQtyChange(idx, Math.max(1, getQty(item) - 1))}
                                >
                                    -
                                </button>
                                <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 'bold', marginRight: '0.25rem' }}>{getQty(item)}</span>
                                <button
                                    style={{
                                        background: theme?.colors?.primary,
                                        color: theme?.colors?.surface,
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 6,
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        padding: '0.25rem 0.5rem',
                                        marginRight: '0.75rem',
                                        fontSize: '1rem',
                                    }}
                                    title={t('increaseQuantity')}
                                    onClick={() => onQtyChange && onQtyChange(idx, getQty(item) + 1)}
                                >
                                    +
                                </button>
                                <span style={{ flex: 1 }}>
                                    {getDisplayName(item)}
                                    {getSelectedOptionName(item) && (
                                        <span style={{ color: theme?.colors?.secondary, marginLeft: 6 }}>
                                            ({getSelectedOptionName(item)})
                                        </span>
                                    )}
                                    <span style={{ color: theme?.colors?.secondary, marginLeft: 6 }}>
                                        ${getItemTotalPrice(item)}
                                    </span>
                                </span>
                                {/* Don't Make button */}
                                <button
                                    style={{
                                        marginLeft: '1rem',
                                        padding: '0.25rem 0.25rem',
                                        background: item.dontMake ? '#ff5722' : theme?.colors?.secondary || '#888',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 6,
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '0.8rem',
                                    }}
                                    onClick={() => {
                                        const updatedItem = { ...item, dontMake: !item.dontMake };
                                        if (onEditItem) onEditItem(idx, updatedItem);
                                    }}
                                    title={item.dontMake ? t('markAsMake') : t('markAsDontMake')}
                                >
                                    {item.dontMake ? t('make') : t('dontMake')}
                                </button>
                                {onRemoveItem && (
                                    <button
                                        style={{
                                            marginLeft: '.2rem',
                                            padding: '0.25rem 0.75rem',
                                            background: theme?.colors?.error || '#d32f2f',
                                            color: theme?.colors?.surface || '#fff',
                                            border: 'none',
                                            borderRadius: theme?.borderRadius || 6,
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'right',
                                            justifyContent: 'right',
                                        }}
                                        onClick={() => onRemoveItem(idx)}
                                        title={t('removeItem')}
                                    >
                                        {/* Trashcan emoji */}
                                        🗑️
                                    </button>
                                )}
                            </div>
                            {item.note && (
                                <div style={{ width: '100%', fontSize: '0.95em', color: theme?.colors?.textSecondary, marginTop: 4, marginLeft: 0, paddingLeft: 0 }}>
                                    <em>{t('note')}: {item.note}</em>
                                </div>
                            )}
                        </li>
                        
                    ))}
                </ul>
            ) : (
                <div style={{ color: theme?.colors?.textSecondary }}></div>
            )}
            
            {items && items.length > 0 && onSendOrder && (
                <button
                    style={{
                        width: '100%',
                        marginTop: '1rem',
                        padding: '0.75rem',
                        background: theme?.colors?.primary || '#1976d2',
                        color: theme?.colors?.surface || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 8,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        boxShadow: theme?.boxShadow,
                    }}
                    onClick={onSendOrder}
                >
                    {t('sendOrder')}
                </button>
            )}
        </div>
    );
}

export default Preorder;
